import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Button, Collapse } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDownload,
    faFolder,
    faFolderOpen,
    faFile,
    faFilePdf,
    faFileImage,
    faFileWord,
    faFileExcel,
    faFilePowerpoint,
    faFileArchive,
    faFileAlt,
    faChevronDown,
    faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import '../Contacts2.css';

const normalizeValue = (value) => String(value || '').trim().toLowerCase();
const normalizeWhitespace = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const getHouseholdKeyFromPath = (path) => {
    const parts = String(path || '')
        .split('/')
        .map(part => normalizeValue(part))
        .filter(Boolean);
    const householdsIdx = parts.indexOf('households');
    return householdsIdx >= 0 ? parts[householdsIdx + 1] || '' : '';
};

const getFolderDisplay = (entry) => {
    const text = normalizeWhitespace(entry?.text);
    const cleaned = text.replace(/\/+$/, '');
    return cleaned || '/';
};

const getParentFolderFromFilePath = (path = '') => {
    const rawParts = String(path || '').split('/').filter(Boolean);
    const householdsIdx = rawParts.findIndex(part => normalizeValue(part) === 'households');
    if (householdsIdx < 0) return '/';

    const afterHousehold = rawParts.slice(householdsIdx + 2);
    if (afterHousehold.length <= 1) return '/';

    const folderParts = afterHousehold.slice(0, -1);
    const folder = folderParts.join('/').trim();
    return folder || '/';
};

const isFolderEntry = (entry) => {
    if (Array.isArray(entry?.items)) return true;
    const text = String(entry?.text || '').trim();
    const path = String(entry?.path || '').trim();
    return text.endsWith('/') || path.endsWith('/');
};

const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric'
    });
};

const isPdfFileName = (value = '') => String(value || '').trim().toLowerCase().endsWith('.pdf');

const getFileExtension = (name = '', path = '') => {
    const source = String(name || path || '').toLowerCase();
    const dotIndex = source.lastIndexOf('.');
    if (dotIndex === -1) return '';
    return source.slice(dotIndex + 1);
};

const getFileIcon = (name = '', path = '') => {
    const ext = getFileExtension(name, path);
    if (ext === 'pdf') return faFilePdf;
    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp', 'tif', 'tiff'].includes(ext)) return faFileImage;
    if (['doc', 'docx'].includes(ext)) return faFileWord;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return faFileExcel;
    if (['ppt', 'pptx'].includes(ext)) return faFilePowerpoint;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return faFileArchive;
    if (['txt', 'rtf', 'md'].includes(ext)) return faFileAlt;
    return faFile;
};

const getFileIconClass = (name = '', path = '') => {
    const ext = getFileExtension(name, path);
    if (ext === 'pdf') return 'contactsDocFileIconPdf';
    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp', 'tif', 'tiff'].includes(ext)) return 'contactsDocFileIconImage';
    return 'contactsDocFileIconDefault';
};

const getUploadedByLine = (uploadedBy = '', uploadDate = '') => {
    const uploadedText = normalizeWhitespace(uploadedBy);
    const dateText = formatDate(uploadDate);

    if (uploadedText && dateText) {
        return `Uploaded by: ${uploadedText} on ${dateText}`;
    }
    if (uploadedText) {
        return `Uploaded by: ${uploadedText}`;
    }
    return '';
};

const ContactsTreeView = ({
    folderEntries = [],
    sourceAccounts = [],
    onDocumentAction,
    isDocumentPending,
    expandCommand,
    onExpansionStateChange
}) => {
    const [expandedHouseholds, setExpandedHouseholds] = useState({});
    const [expandedFolders, setExpandedFolders] = useState({});

    const householdDisplayMap = useMemo(() => {
        const map = new Map();
        (sourceAccounts || []).forEach((account) => {
            const label = normalizeWhitespace(account);
            const key = normalizeValue(label);
            if (key && !map.has(key)) map.set(key, label);
        });
        return map;
    }, [sourceAccounts]);

    const hierarchy = useMemo(() => {
        const householdMap = new Map();

        (folderEntries || []).forEach((entry) => {
            const householdKey = getHouseholdKeyFromPath(entry?.path);
            if (!householdKey) return;

            const householdLabel = householdDisplayMap.get(householdKey) || householdKey;
            if (!householdMap.has(householdKey)) {
                householdMap.set(householdKey, {
                    key: householdKey,
                    household: householdLabel,
                    _folderMap: new Map()
                });
            }

            const householdNode = householdMap.get(householdKey);

            if (isFolderEntry(entry)) {
                const folder = getFolderDisplay(entry);
                const folderPath = String(entry?.path || '');
                const items = Array.isArray(entry?.items) ? entry.items : [];
                const folderKey = `${householdKey}::${normalizeValue(folderPath || folder)}`;

                if (!householdNode._folderMap.has(folderKey)) {
                    householdNode._folderMap.set(folderKey, {
                        key: folderKey,
                        folder,
                        items: []
                    });
                }

                const folderNode = householdNode._folderMap.get(folderKey);
                items.forEach((item) => {
                    folderNode.items.push({
                        fileName: item?.text || '',
                        filePath: item?.path || '',
                        uploadedBy: item?.uploadedBy || '',
                        uploadDate: item?.uploadDate || '',
                        description: item?.description || '',
                        makePrivate: !!item?.makePrivate
                    });
                });
                return;
            }

            const folder = getParentFolderFromFilePath(entry?.path);
            const folderKey = `${householdKey}::${normalizeValue(folder)}`;

            if (!householdNode._folderMap.has(folderKey)) {
                householdNode._folderMap.set(folderKey, {
                    key: folderKey,
                    folder,
                    items: []
                });
            }

            const folderNode = householdNode._folderMap.get(folderKey);
            folderNode.items.push({
                fileName: entry?.text || '',
                filePath: entry?.path || '',
                uploadedBy: entry?.uploadedBy || '',
                uploadDate: entry?.uploadDate || '',
                description: entry?.description || '',
                makePrivate: !!entry?.makePrivate
            });
        });

        return Array.from(householdMap.values())
            .map((householdNode) => {
                const allFolders = Array.from(householdNode._folderMap.values())
                    .map((folderNode) => ({
                        ...folderNode,
                        items: [...folderNode.items].sort((a, b) => a.fileName.localeCompare(b.fileName))
                    }))
                    .filter((folderNode) => folderNode.items.length > 0)
                    .sort((a, b) => a.folder.localeCompare(b.folder));

                const rootFolder = allFolders.find((folderNode) => folderNode.folder === '/');
                const rootItems = rootFolder ? rootFolder.items : [];
                const folders = allFolders.filter((folderNode) => folderNode.folder !== '/');

                return {
                    key: householdNode.key,
                    household: householdNode.household,
                    rootItems,
                    folders
                };
            })
            .filter((householdNode) => householdNode.rootItems.length > 0 || householdNode.folders.length > 0)
            .sort((a, b) => a.household.localeCompare(b.household));
    }, [folderEntries, householdDisplayMap]);

    const householdKeys = useMemo(() => hierarchy.map(item => item.key), [hierarchy]);
    const folderKeys = useMemo(
        () => hierarchy.flatMap(item => item.folders.map(folder => folder.key)),
        [hierarchy]
    );

    useEffect(() => {
        const keys = new Set(householdKeys);
        setExpandedHouseholds(prev => {
            const next = {};
            keys.forEach((key) => {
                next[key] = prev[key] !== undefined ? prev[key] : true;
            });
            return next;
        });
    }, [householdKeys]);

    useEffect(() => {
        const keys = new Set(folderKeys);
        setExpandedFolders(prev => {
            const next = {};
            keys.forEach((key) => {
                next[key] = prev[key] !== undefined ? prev[key] : true;
            });
            return next;
        });
    }, [folderKeys]);

    useEffect(() => {
        if (!expandCommand?.nonce) return;

        const expand = expandCommand.mode !== 'collapse';
        setExpandedHouseholds(() => {
            const next = {};
            householdKeys.forEach((key) => { next[key] = expand; });
            return next;
        });
        setExpandedFolders(() => {
            const next = {};
            folderKeys.forEach((key) => { next[key] = expand; });
            return next;
        });
    }, [expandCommand, householdKeys, folderKeys]);

    const toggleHousehold = useCallback((householdKey) => {
        setExpandedHouseholds(prev => ({
            ...prev,
            [householdKey]: !prev[householdKey]
        }));
    }, []);

    const toggleFolder = useCallback((folderKey) => {
        setExpandedFolders(prev => ({
            ...prev,
            [folderKey]: !prev[folderKey]
        }));
    }, []);

    const renderFileRow = useCallback((item, keyPrefix) => {
        const pending = typeof isDocumentPending === 'function'
            ? isDocumentPending(item.fileName, item.filePath)
            : false;
        const fileLinkAllowed = isPdfFileName(item.fileName);

        return (
            <div
                key={`${keyPrefix}::${normalizeValue(item.filePath || item.fileName)}`}
                className="contactsDocFileRow"
            >
                <div className="contactsDocFileMain">
                    <div className="contactsDocFileHeader">
                        <Button
                            size="sm"
                            color="primary"
                            // outline
                            disabled={!item.filePath || pending}
                            title="Download"
                            className="contactsDocDownloadBtn"
                            onClick={() => {
                                if (typeof onDocumentAction === 'function') {
                                    onDocumentAction({
                                        name: item.fileName,
                                        path: item.filePath,
                                        source: 'download'
                                    });
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faDownload} />
                        </Button>
                        <FontAwesomeIcon
                            icon={getFileIcon(item.fileName, item.filePath)}
                            className={`contactsDocFileIcon ${getFileIconClass(item.fileName, item.filePath)}`}
                        />
                        {fileLinkAllowed ? (
                            <Button
                                color="link"
                                className="contactsDocFileLink"
                                disabled={!item.filePath || pending}
                                onClick={() => {
                                    if (typeof onDocumentAction === 'function') {
                                        onDocumentAction({
                                            name: item.fileName,
                                            path: item.filePath,
                                            source: 'file'
                                        });
                                    }
                                }}
                            >
                                {item.fileName}
                            </Button>
                        ) : (
                            <span className="contactsDocFileName">{item.fileName}</span>
                        )}
                    </div>

                    {normalizeWhitespace(item.description) && (
                        <div className="contactsDocFileDescription">
                            {item.description}
                        </div>
                    )}

                    {getUploadedByLine(item.uploadedBy, item.uploadDate) && (
                        <div className="contactsDocFileUploaded">
                            {getUploadedByLine(item.uploadedBy, item.uploadDate)}
                        </div>
                    )}
                </div>
            </div>
        );
    }, [isDocumentPending, onDocumentAction]);

    const expansionSummary = useMemo(() => {
        const totalExpandable = householdKeys.length + folderKeys.length;
        if (totalExpandable === 0) {
            return {
                hasExpandable: false,
                allExpanded: true
            };
        }

        const allHouseholdsExpanded = householdKeys.every(key => expandedHouseholds[key] !== false);
        const allFoldersExpanded = folderKeys.every(key => expandedFolders[key] !== false);

        return {
            hasExpandable: true,
            allExpanded: allHouseholdsExpanded && allFoldersExpanded
        };
    }, [householdKeys, folderKeys, expandedHouseholds, expandedFolders]);

    useEffect(() => {
        if (typeof onExpansionStateChange === 'function') {
            onExpansionStateChange(expansionSummary);
        }
    }, [onExpansionStateChange, expansionSummary]);

    if (hierarchy.length === 0) {
        return (
            <div className="contactsDocGridEmpty text-muted">
                No documents available.
            </div>
        );
    }

    return (
        <div className="contactsDocGrid">
            {hierarchy.map((householdNode) => {
                const householdOpen = expandedHouseholds[householdNode.key] !== false;

                return (
                    <div key={householdNode.key} className="contactsDocHouseholdBlock">
                        <Button
                            color="link"
                            className="contactsDocHouseholdToggle"
                            onClick={() => toggleHousehold(householdNode.key)}
                        >
                            <FontAwesomeIcon
                                icon={householdOpen ? faChevronDown : faChevronRight}
                                className="contactsDocCaret"
                            />
                            <FontAwesomeIcon
                                icon={householdOpen ? faFolderOpen : faFolder}
                                className="contactsDocFolderIcon"
                            />
                            <span>{householdNode.household}</span>
                        </Button>

                        <Collapse isOpen={householdOpen}>
                            <div className="contactsDocHouseholdContent">
                                {householdNode.rootItems.length > 0 && (
                                    <div className="contactsDocFileList">
                                        {householdNode.rootItems.map((item) => renderFileRow(item, `${householdNode.key}::root`))}
                                    </div>
                                )}
                                {householdNode.folders.map((folderNode) => {
                                    const folderOpen = expandedFolders[folderNode.key] !== false;

                                    return (
                                        <div key={folderNode.key} className="contactsDocFolderBlock">
                                            <Button
                                                color="link"
                                                className="contactsDocFolderToggle"
                                                onClick={() => toggleFolder(folderNode.key)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={folderOpen ? faChevronDown : faChevronRight}
                                                    className="contactsDocCaret"
                                                />
                                                <FontAwesomeIcon
                                                    icon={folderOpen ? faFolderOpen : faFolder}
                                                    className="contactsDocFolderIcon"
                                                />
                                                <span>{folderNode.folder}</span>
                                            </Button>

                                            <Collapse isOpen={folderOpen}>
                                                <div className="contactsDocFileList">
                                                    {folderNode.items.map((item) => renderFileRow(item, folderNode.key))}
                                                </div>
                                            </Collapse>
                                        </div>
                                    );
                                })}
                            </div>
                        </Collapse>
                    </div>
                );
            })}
        </div>
    );
};

export default ContactsTreeView;
